import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import Calendar from '../form-controls/Calendar';
import StatusHelper from '../../helpers/StatusHelper';
import { useQuotesFilter } from '../../hooks/QuotesFilterContext';
import DateHelper from '../../helpers/DateHelper';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import TablePagination from '@mui/material/TablePagination';

const calculateDateRange = (fromDateObj, toDateObj) => {
    const timeDifference = toDateObj.getTime() - fromDateObj.getTime();
    const dayDifference = timeDifference / (1000 * 60 * 60 * 24);
    return Math.round(dayDifference);
};

const QuotesFilter = (props) => {
    const { filters, updateFilter } = useQuotesFilter();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const lineStatusFilters = props.availableStatuses
        .filter(x => x.isQuoteStatus === true && x.shown === true)
        .map(x => x.description)?.sort();

    const lineStatusOptions = lineStatusFilters.map((item) => ({
        value: item,
        label: StatusHelper.formatQuoteStatus(item, props.localized),
    }));

    const sortOptions = [
        { value: '0', label: props.localized.NewestFirst },
        { value: '1', label: props.localized.OldestFirst },
    ];

    const defaultsSort = () => {
        const sortValue = parseInt(filters.sort, 10);
        const defaultOption = sortOptions.find(
            (option) => parseInt(option.value, 10) === sortValue
        ) || sortOptions[0];
        return { label: defaultOption.label, value: defaultOption.value };
    };

    const [sortSelectValue, setSortSelectValue] = useState(() => defaultsSort());


    useEffect(() => {
        setSortSelectValue(defaultsSort());
    }, [filters.sort, props.localized.NewestFirst, props.localized.OldestFirst]);


    const handleEnter = (e) => {
        if (e.key === 'Enter') props.fetchOrders?.();
    };

    const handleSelectChange = (option) => {
        const obj = { value: parseInt(option.value, 10), label: option.label }; 
        setSortSelectValue(obj);
        updateFilter('sort', option.value);
    };

    const defaults = filters.status.map((item) => ({
        value: item,
        label: StatusHelper.formatStatus(item, props.localized), 
    }));

    const handleStatusChange = (statusOptions) => {
        const statusValues = statusOptions ? statusOptions.map((o) => o.value) : [];
        updateFilter('status', statusValues);
    };

    const handleFromDateChange = (date) => {
        updateFilter('fromFullDate', date);
        updateFilter('fromDate', date.toISOString().split('T')[0]);
        updateFilter('dateRange', calculateDateRange(date, props.toFullDate));
    };

    const handleToDateChange = (date) => {
        if (!DateHelper.isValidDate(date)) return;
        updateFilter('toFullDate', date);
        updateFilter('toDate', date.toISOString().split('T')[0]);
        updateFilter('dateRange', calculateDateRange(props.fromFullDate, date));
    };

    const maxFromDate = props.maxFromDate;

    const customSelectStyles = {
        option: (provided) => ({
            ...provided,
            fontSize: isMobile ? '1.1rem' : '1.5rem',
        }),
        control: (base) => ({
            ...base,
            minHeight: isMobile ? 42 : 35,
            fontSize: isMobile ? '1.05rem' : '1.4rem',
        }),
    };

    const PaginationRow = () => {
        const total = Number.isFinite(props.totalCount) ? props.totalCount : 0;
        const page = Math.max(0, props.paginationModel?.page ?? 0); // 0-based
        const pageSize = props.paginationModel?.pageSize ?? 25;
        const options = props.rowsPerPageOptions ?? [10, 25, 50, 100];

        const handleChangePage = (_evt, newPage) => {
            props.setPaginationModel?.({ ...props.paginationModel, page: newPage });
        };
        const handleChangeRowsPerPage = (evt) => {
            const newSize = parseInt(evt.target.value, 10);
            props.setPaginationModel?.({ ...props.paginationModel, pageSize: newSize, page: 0 });
        };

        return (
            <div className="row" style={{ marginTop: 8 }}>
                <div className="col-12 d-flex justify-content-end">
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        rowsPerPage={pageSize}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={options}
                        labelRowsPerPage={props.localized?.RowsPerPage ?? 'Rows per page'}
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}–${to} ${props.localized?.Of ?? 'of'} ${count}`
                        }
                        sx={{
                            color: 'white',
                            '& .MuiTablePagination-toolbar': { minHeight: 36, px: 0.5 },
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { color: 'white' },
                            '& .MuiTablePagination-actions button': { color: 'white' },
                            '& .MuiInputBase-input': { color: 'white' },
                            '& .MuiSvgIcon-root': { color: 'white' },
                        }}
                    />
                </div>
            </div>
        );
    };

    const renderDesktop = () => (
        <div>
            <div
                className="container-fluid"
                style={{
                    background: "#172b42",
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                    paddingBottom: "15px",
                    width: "100%",
                    marginLeft: "0px"
                }}
            >
                <div className="row gy-2">
                    <div className="col-xl-2 col-lg-2 d-flex">
                        <div style={{ 'max-width': '100%', 'minWidth': '100%' }}>
                            <div className="label" style={{ color: "white" }}>{props.localized.SortByLabel}</div>
                            <div className="select-wrapper">
                                <Select
                                    id={'ddlSetSort'}
                                    styles={customSelectStyles}
                                    options={sortOptions}
                                    defaultValue={defaultsSort()}
                                    onChange={handleSelectChange}
                                    value={sortSelectValue}
                                    isSearchable={false}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-2 col-lg-2">
                        <div className="label" style={{ color: "white" }}>{props.localized.Status}</div>
                        <div className="fele-content">
                            <div className="select-wrapper">
                                <Select
                                    id={'ddlSetStatus'}
                                    styles={customSelectStyles}
                                    isMulti
                                    options={lineStatusOptions}
                                    value={defaults}
                                    onChange={handleStatusChange}
                                    placeholder={props.localized.SelectStatus}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-2 col-lg-2" data-cy="order-from-date">
                        <div className="label" style={{ color: "white" }}>{props.localized.FromDate}</div>
                        <div className="fele-content">
                            <div className="input-wrapper">
                                <Calendar
                                    color="white"
                                    date={filters.fromFullDate}
                                    minDate={new Date().setDate(new Date().getDate() - maxFromDate)}
                                    callBackFunction={handleFromDateChange}
                                    showIcon={true}
                                    dateFormat={props.dateFormat}
                                    localized={props.localized}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-2 col-lg-2" data-cy="order-to-date">
                        <div className="label" style={{ color: "white" }}>{props.localized.ToDate}</div>
                        <div className="fele-content">
                            <div className="input-wrapper">
                                <Calendar
                                    color="white"
                                    date={filters.toFullDate}
                                    minDate={new Date().setDate(new Date().getDate() - maxFromDate)}
                                    callBackFunction={handleToDateChange}
                                    dateFormat={props.dateFormat}
                                    showIcon={true}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-2 col-lg-2">
                        <div className="label" style={{ color: "white" }}>{props.localized.Search}</div>
                        <div className="form-group orderSearch">
                            <input
                                data-cy="order-search"
                                type="text"
                                placeholder={props.localized.Search}
                                onChange={(e) => updateFilter('numberFilter', e.target.value)}
                                onKeyPress={handleEnter}
                                value={filters.numberFilter}
                            />
                        </div>
                    </div>

                    <div className="col-xl-2">
                        <label></label>
                        <div className="row">
                            <div className="col-xl-6 col-lg-10 col-md-10 col-sm-10 col-xs-6 gx-2 gy-2">
                                <div className="button-wrapper float-end" style={{ minWidth: "8.2rem" }}>
                                    <button
                                        data-cy="order-submit-search"
                                        className="button search-button button-alt"
                                        style={{ height: "3.2rem", width: "6.2rem" }}
                                        onClick={props.fetchOrders}
                                    >
                                        {props.localized.Search}
                                    </button>
                                </div>
                            </div>
                            <div className="col-xl-6 col-lg-2 col-md-2 col-sm-2 col-xs-6 gx-2 gy-2">
                                <div className="button-wrapper float-end" style={{ minWidth: "9.2rem" }}>
                                    <button
                                        data-cy="order-submit-search"
                                        className="button search-button button-alt"
                                        style={{ height: "3.2rem" }}
                                        onClick={props.resetFilters}
                                    >
                                        {props.localized.ResetFilters}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div> 
            </div> 

            <div className="seperator"></div>
            {props.listView && <PaginationRow />}
        </div>
    );

    const renderMobile = () => (
        <div>
            <div
                className="container-fluid"
                style={{
                    background: "#172b42",
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    padding: "12px",
                    width: "100%",
                    marginLeft: 0,
                }}
            >
                <div
                    className="d-flex"
                    style={{
                        gap: 8,
                        alignItems: 'flex-start',
                        overflow: 'visible',
                        flexWrap: 'nowrap',
                    }}
                >
                    <div style={{ flex: '0 0 60%', minWidth: 0, position: 'relative' }}>
                        <div className="label" style={{ color: "white", marginBottom: 6 }}>
                            {props.localized.Search}
                        </div>
                        <div className="form-group orderSearch">
                            <input
                                data-cy="order-search"
                                type="text"
                                placeholder={props.localized.Search}
                                onChange={(e) => updateFilter('numberFilter', e.target.value)}
                                onKeyPress={handleEnter}
                                value={filters.numberFilter}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>

                    <div style={{ flex: '0 0 40%', minWidth: 0, position: 'relative' }}>
                        <div className="label" style={{ color: "white", marginBottom: 6 }}>
                            {props.localized.Status}
                        </div>
                        <Select
                            id={'ddlSetStatus'}
                            styles={customSelectStyles}
                            isMulti
                            options={lineStatusOptions}
                            value={defaults}
                            onChange={handleStatusChange}
                            placeholder={props.localized.SelectStatus}
                        />
                    </div>
                </div>
                <div
                    className="d-flex mt-2"
                    style={{
                        gap: 8,
                        alignItems: 'flex-start',
                        overflow: 'visible',
                        flexWrap: 'nowrap',
                    }}
                >
                    <div style={{ flex: '0 0 50%', minWidth: 0, position: 'relative' }}>
                        <div className="label" style={{ color: "white", marginBottom: 6 }}>
                            {props.localized.FromDate}
                        </div>
                        <Calendar
                            color="white"
                            date={filters.fromFullDate}
                            minDate={new Date().setDate(new Date().getDate() - maxFromDate)}
                            callBackFunction={handleFromDateChange}
                            showIcon={true}
                            dateFormat={props.dateFormat}
                            localized={props.localized}
                        />
                    </div>

                    <div style={{ flex: '0 0 50%', minWidth: 0, position: 'relative' }}>
                        <div className="label" style={{ color: "white", marginBottom: 6 }}>
                            {props.localized.ToDate}
                        </div>
                        <Calendar
                            color="white"
                            date={filters.toFullDate}
                            minDate={new Date().setDate(new Date().getDate() - maxFromDate)}
                            callBackFunction={handleToDateChange}
                            dateFormat={props.dateFormat}
                            showIcon={true}
                        />
                    </div>
                </div>
                <div className="mt-2" style={{ position: 'relative' }}>
                    <div className="label" style={{ color: "white", marginBottom: 6 }}>
                        {props.localized.SortByLabel}
                    </div>
                    <Select
                        id={'ddlSetSort'}
                        styles={customSelectStyles}
                        options={sortOptions}
                        defaultValue={defaultsSort()}
                        onChange={handleSelectChange}
                        value={sortSelectValue}
                        isSearchable={false}
                    />
                </div>
                <div className="mt-3 d-flex" style={{ gap: 8 }}>
                    <button
                        data-cy="order-submit-search"
                        className="button search-button button-alt"
                        style={{ height: "3.2rem", flex: 1 }}
                        onClick={props.fetchOrders}
                    >
                        {props.localized.Search}
                    </button>
                    <button
                        data-cy="order-submit-search"
                        className="button search-button button-alt"
                        style={{ height: "3.2rem", flex: 1 }}
                        onClick={props.resetFilters}
                    >
                        {props.localized.ResetFilters}
                    </button>
                </div>
                <PaginationRow />
            </div>
        </div>
    );

    return isMobile ? renderMobile() : renderDesktop();
};

export default QuotesFilter;